import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy, Loader2, X } from "lucide-react";
import { licitacionesApi, type CloneLicitacionParams } from "@/lib/api/licitaciones";
import { showToast } from "@/lib/toast";
import type { Licitacion } from "@/types/licitacion";

interface CloneLicitacionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  sourceId: string;
  sourceTitle: string;
}

/**
 * Mejora H (changelog cliente): diálogo para clonar una licitación existente
 * como plantilla de una nueva convocatoria.
 *
 * Solicitado en la reunión 2026-04-10 (Rec50): los CIMI terminan el contrato
 * el 31/12/2026 y el equipo quería arrancar el borrador antes de que se
 * publique el nuevo pliego, partiendo del de Genil (Granada Dic-2025). Los
 * documentos NO se clonan: cuando se publique el nuevo pliego se sube al
 * clon y se reprocesa.
 */
export function CloneLicitacionDialog({
  isOpen,
  onClose,
  sourceId,
  sourceTitle,
}: CloneLicitacionDialogProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [titulo, setTitulo] = useState("");
  const [referencia, setReferencia] = useState("");
  const [includeProposals, setIncludeProposals] = useState(true);
  const [includeEtiquetas, setIncludeEtiquetas] = useState(true);
  const [includeNotas, setIncludeNotas] = useState(false);

  const mutation = useMutation<Licitacion, Error, CloneLicitacionParams>({
    mutationFn: (params) => licitacionesApi.clone(sourceId, params),
    onSuccess: (clone) => {
      queryClient.invalidateQueries({ queryKey: ["licitaciones"] });
      showToast.success("Licitación clonada correctamente");
      onClose();
      // Navegamos al detalle del clon para que el usuario pueda empezar a
      // trabajar inmediatamente con la nueva licitación.
      navigate(`/licitaciones/${clone.id}`);
    },
    onError: (error) => {
      showToast.error(error.message || "Error al clonar la licitación");
    },
  });

  // Al abrir, sugerimos un título arrastrado del origen para ahorrar al
  // usuario tener que reescribirlo. Lo dejamos editable.
  useEffect(() => {
    if (isOpen) {
      setTitulo(`Copia de ${sourceTitle}`);
      setReferencia("");
      setIncludeProposals(true);
      setIncludeEtiquetas(true);
      setIncludeNotas(false);
    }
  }, [isOpen, sourceTitle]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    const params: CloneLicitacionParams = {
      include_proposals: includeProposals,
      include_etiquetas: includeEtiquetas,
      include_notas: includeNotas,
    };
    const trimmedTitulo = titulo.trim();
    if (trimmedTitulo && trimmedTitulo !== `Copia de ${sourceTitle}`) {
      params.titulo = trimmedTitulo;
    } else if (trimmedTitulo) {
      params.titulo = trimmedTitulo;
    }
    const trimmedRef = referencia.trim();
    if (trimmedRef) {
      params.referencia = trimmedRef;
    }
    mutation.mutate(params);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Clonar licitación como plantilla"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Copy className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              Usar como plantilla
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Cerrar"
            disabled={mutation.isPending}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-gray-600">
            Se creará una nueva licitación copiando los datos de{" "}
            <span className="font-medium text-gray-900">{sourceTitle}</span>.
            Empezará en estado <span className="font-medium">Borrador</span>{" "}
            sin fechas ni resultados. Los documentos{" "}
            <span className="font-medium">no se clonan</span>: cuando se
            publique el nuevo pliego, lo subes al clon y se procesa
            automáticamente.
          </p>

          <div>
            <label
              htmlFor="clone-titulo"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Título de la nueva licitación
            </label>
            <input
              id="clone-titulo"
              type="text"
              value={titulo}
              onChange={(e) => setTitulo(e.target.value)}
              disabled={mutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            />
          </div>

          <div>
            <label
              htmlFor="clone-referencia"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Referencia / expediente{" "}
              <span className="text-gray-400 font-normal">opcional</span>
            </label>
            <input
              id="clone-referencia"
              type="text"
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
              placeholder="Ej: CIMI-2026-001"
              disabled={mutation.isPending}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
            />
            <p className="text-xs text-gray-500 mt-1">
              Si lo dejas vacío, la nueva licitación no tendrá referencia hasta
              que conozcas el expediente real.
            </p>
          </div>

          <fieldset className="space-y-2 pt-2 border-t border-gray-100">
            <legend className="text-sm font-medium text-gray-700 pb-2">
              Qué se clona
            </legend>

            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeProposals}
                onChange={(e) => setIncludeProposals(e.target.checked)}
                disabled={mutation.isPending}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                Propuestas y secciones
                <span className="block text-xs text-gray-500 mt-0.5">
                  Recomendado. Cada propuesta se clona como borrador versión 1
                  con su contenido, sin arrastrar el historial del crítico.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeEtiquetas}
                onChange={(e) => setIncludeEtiquetas(e.target.checked)}
                disabled={mutation.isPending}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Etiquetas</span>
            </label>

            <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeNotas}
                onChange={(e) => setIncludeNotas(e.target.checked)}
                disabled={mutation.isPending}
                className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>
                Notas internas
                <span className="block text-xs text-gray-500 mt-0.5">
                  Por defecto no, para no propagar comentarios de la convocatoria
                  anterior.
                </span>
              </span>
            </label>
          </fieldset>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-200">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={mutation.isPending || !titulo.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Clonando...
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                Clonar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CloneLicitacionDialog;
